import React, { useState } from 'react';
import { CheckCircle2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { products } from '../data/mockData';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { productsTranslations } from '../i18n/productsTranslations';

export const Products = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Всі рішення' },
    { id: 'private-houses', label: 'Для приватних будинків' },
    { id: 'commercial', label: 'Для комерційних об\'єктів' },
    { id: 'pura-4', label: 'PURA-4' },
    { id: 'pura-6', label: 'PURA-6' },
    { id: 'pura-9', label: 'PURA-9' },
    { id: 'custom', label: 'Нестандартні вироби' }
  ];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('productPage.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('productPage.subtitle')}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.id)}
                style={selectedCategory === category.id ? { backgroundColor: 'rgb(1, 157, 180)' } : {}}
                className={selectedCategory === category.id ? 'text-white' : ''}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16">
            {filteredProducts.map((product) => (
              <div key={product.id} className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Image */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[500px]">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 text-gray-900 hover:bg-white">
                      {product.category === 'private-houses' && 'Приватні будинки'}
                      {product.category === 'commercial' && 'Комерційні об\'єкти'}
                      {product.category === 'pura-4' && 'PURA-4'}
                      {product.category === 'pura-6' && 'PURA-6'}
                      {product.category === 'pura-9' && 'PURA-9'}
                      {product.category === 'custom' && 'Нестандартні вироби'}
                    </Badge>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{product.title}</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{product.description}</p>

                  {/* Price */}
                  {product.price && (
                    <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                      <div className="flex items-baseline space-x-2">
                        <span className="text-3xl font-bold" style={{ color: 'rgb(1, 157, 180)' }}>
                          {product.price.toLocaleString('uk-UA')} грн
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Specifications */}
                  <Card className="mb-6 border-2" style={{ borderColor: 'rgb(1, 157, 180, 0.2)' }}>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center space-x-2">
                        <Info className="h-5 w-5" style={{ color: 'rgb(1, 157, 180)' }} />
                        <span>Технічні характеристики</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-2">
                        {Object.entries(product.specifications).map(([key, value]) => (
                          <div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                            <dt className="text-gray-600 text-sm">
                              {key === 'capacity' && 'Потужність'}
                              {key === 'material' && 'Матеріал'}
                              {key === 'warranty' && 'Гарантія'}
                              {key === 'volume' && 'Об\'єм'}
                              {key === 'certification' && 'Сертифікація'}
                              {key === 'customization' && 'Кастомізація'}
                              {key === 'delivery' && 'Терміни поставки'}
                            </dt>
                            <dd className="text-gray-900 font-medium text-sm">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </CardContent>
                  </Card>

                  {/* Accordion for Features and Applications */}
                  <Accordion type="single" collapsible className="mb-6">
                    <AccordionItem value="features">
                      <AccordionTrigger className="text-lg font-semibold">Переваги та особливості</AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-3">
                          {product.features.map((feature, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="applications">
                      <AccordionTrigger className="text-lg font-semibold">Сфери застосування</AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-3">
                          {product.applications.map((application, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(1, 157, 180)' }} />
                              <span className="text-gray-700">{application}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <Button
                    asChild
                    size="lg"
                    style={{ backgroundColor: 'rgb(1, 157, 180)' }}
                    className="w-full text-white hover:opacity-90 transition-opacity"
                  >
                    <Link to="/contacts">Запитати ціну та наявність</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Потрібна допомога у виборі?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Наші фахівці допоможуть підібрати оптимальне рішення для вашого об'єкта
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              style={{ backgroundColor: 'rgb(1, 157, 180)' }}
              className="text-white hover:opacity-90 transition-opacity"
            >
              <Link to="/contacts">Безкоштовна консультація</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/gallery">Переглянути реалізовані проєкти</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};